'use client';

import React from 'react';
import classNames from 'classnames';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { StoryObject } from '@benzinga/advanced-news-manager';
import { ErrorBoundary } from '@benzinga/core-ui';
import { useHorizontalScroll, useScrollShadow } from '@benzinga/hooks';
import { ArticleCard } from './ArticleCard';
import { BlockLayout } from './BlockLayout';

export type NewsLayoutLayout =
  | 'horizontal-split'
  | 'vertical-hero-grid'
  | 'vertical-grid'
  | 'horizontal-cards'
  | 'horizontal-carousel';

interface NewsLayoutProps {
  news: StoryObject[];
  layout: NewsLayoutLayout;
  title: string;
  subtitle?: string;
  actionLabel?: string;
  actionLink?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'clean' | 'dark';
  highPriority?: boolean;
  fillHeight?: boolean;
  showTicker?: boolean;
  sponsored?: boolean;
}

export const NewsLayout = ({
  news,
  layout,
  title,
  subtitle,
  actionLabel = 'See More',
  actionLink,
  icon,
  variant = 'default',
  highPriority,
  fillHeight,
  showTicker = true,
  sponsored,
}: NewsLayoutProps) => {
  if (!news || news.length === 0) return null;

  const renderContent = () => {
    switch (layout) {
      case 'horizontal-split':
        return <HorizontalSplitLayout news={news} variant={variant} showTicker={showTicker} />;
      case 'vertical-hero-grid':
        return (
          <VerticalHeroGridLayout
            variant={variant}
            news={news}
            highPriority={highPriority}
            fillHeight={fillHeight}
            showTicker={showTicker}
          />
        );
      case 'vertical-grid':
        return <VerticalGridLayout news={news} showTicker={showTicker} />;
      case 'horizontal-cards':
        return <HorizontalCardsLayout news={news} sponsored={sponsored} />;
      case 'horizontal-carousel':
        return <HorizontalCarouselLayout news={news} showTicker={showTicker} />;
      default:
        return null;
    }
  };

  return (
    <ErrorBoundary name={`news-widget-${layout}`}>
      <BlockLayout
        title={title}
        subtitle={subtitle}
        actionLabel={actionLabel}
        actionLink={actionLink}
        variant={variant}
        icon={icon}
        fillHeight={fillHeight}
        headerClassName="uppercase"
        sponsored={sponsored}
      >
        {renderContent()}
      </BlockLayout>
    </ErrorBoundary>
  );
};

const HorizontalSplitLayout = ({
  news,
  variant,
  showTicker,
}: {
  news: StoryObject[];
  variant: NewsLayoutProps['variant'];
  showTicker: boolean;
}) => {
  const [mainStory, ...otherStories] = news;

  return (
    <div className="flex flex-col lg:flex-row gap-2.5 min-h-[400px]">
      {mainStory && (
        <div className="lg:w-1/2 flex-shrink-0">
          <ArticleCard
            article={mainStory}
            variant="split-main"
            showTicker={showTicker}
            className={variant === 'dark' ? 'border border-gray-600 rounded-lg' : ''}
          />
        </div>
      )}
      {otherStories.length > 0 && (
        <div className={classNames('lg:w-1/2 flex flex-col gap-2.5', !showTicker && 'justify-start')}>
          {otherStories.slice(0, 4).map((story, index) => (
            <ArticleCard
              article={story}
              variant="split-side"
              showTicker={showTicker}
              className={variant === 'dark' ? 'border border-gray-600 rounded-lg' : ''}
              key={`horizontal-split-layout_top-story-side-card-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const getGridColumnsClass = (storyCount: number) => {
  if (storyCount === 1) return 'grid-cols-1';
  if (storyCount === 2) return 'grid-cols-2';
  if (storyCount === 3) return 'grid-cols-2 @md:grid-cols-4';
  return 'grid-cols-2 @md:grid-cols-4';
};

const VerticalHeroGridLayout = ({
  news,
  highPriority,
  variant,
  fillHeight,
  showTicker,
}: {
  news: StoryObject[];
  highPriority?: boolean;
  variant: NewsLayoutProps['variant'];
  fillHeight?: boolean;
  showTicker: boolean;
}) => {
  const [mainStory, ...otherStories] = news;
  const visibleStories = otherStories.slice(0, 4);
  const gridColumnsClass = getGridColumnsClass(visibleStories.length);
  const isDark = variant === 'dark';

  return (
    <div className={classNames('@container', { 'flex-1 flex flex-col': fillHeight })}>
      {mainStory && (
        <div
          className={classNames(
            'mb-2.5 shadow-sm rounded-md overflow-hidden bg-white border',
            isDark ? 'border-gray-600' : 'border-bzblue-400',
            fillHeight && 'flex-1 flex flex-col',
          )}
        >
          <ArticleCard
            article={mainStory}
            variant="vertical-main"
            highPriority={highPriority}
            fillHeight={fillHeight}
            showTicker={showTicker}
          />
        </div>
      )}
      {visibleStories.length > 0 && (
        <div
          className={`grid ${gridColumnsClass} gap-2.5 auto-rows-min items-stretch`}
        >
          {visibleStories.map((story, index) => (
            <div
              key={`vertical-hero-grid-layout_news-card-${story.id}`}
              className={classNames(
                'rounded-md overflow-hidden bg-white border w-full h-full self-stretch',
                isDark ? 'border-gray-600' : 'border-bzblue-400',
                {
                  'col-span-2': visibleStories.length === 3 && index === 2,
                },
              )}
            >
              <ArticleCard
                article={story}
                variant="vertical-compact"
                highPriority={highPriority}
                showTicker={showTicker}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const VerticalGridLayout = ({ news, showTicker }: { news: StoryObject[]; showTicker: boolean }) => {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {news.slice(0, 10).map(story => (
          <div key={`vertical-grid-layout_card-${story.id}`}>
            <ArticleCard article={story} variant="vertical-compact" fullRoundedImage showTicker={showTicker} />
          </div>
        ))}
      </div>
    </>
  );
};

const HorizontalCardsLayout = ({ news, sponsored }: { news: StoryObject[]; sponsored?: boolean }) => {
  const visibleNews = sponsored ? news : news.slice(0, 3);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
      {visibleNews.map(story => (
        <ArticleCard
          key={`horizontal-cards-layout_exclusive-card-${story.id}`}
          article={story}
          variant="horizontal"
          sponsored={sponsored}
        />
      ))}
    </div>
  );
};

const HorizontalCarouselLayout = ({ news, showTicker }: { news: StoryObject[]; showTicker: boolean }) => {
  const { scrollLeft, scrollRef, scrollRight } = useHorizontalScroll<HTMLDivElement>({ scrollFraction: 0.75 });
  const { showStart, showEnd } = useScrollShadow(scrollRef);
  const isAtStart = !showStart;
  const isAtEnd = !showEnd;

  const desktopButtonClass = (isDisabled: boolean) =>
    classNames(
      'hidden md:inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bzblue-500 hover:bg-white',
      isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md',
    );

  const mobileButtonClass = (isDisabled: boolean) =>
    classNames(
      'flex-1 inline-flex md:hidden items-center justify-center h-10 rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bzblue-500',
      isDisabled ? 'opacity-50 cursor-not-allowed' : 'active:bg-slate-50',
    );

  return (
    <div className="relative">
      <div
        className="flex gap-4 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:pl-16 md:pr-16 scroll-px-12 md:scroll-px-16"
        ref={scrollRef}
        aria-label="Horizontal carousel stories"
      >
        {news.slice(0, 10).map(story => (
          <div key={`carousel-card-${story.id}`} className="w-[190px] flex-shrink-0 md:w-[180px]">
            <ArticleCard article={story} variant="vertical-compact" fullRoundedImage showTicker={showTicker} />
          </div>
        ))}
      </div>

      {!isAtStart && (
        <div className="pointer-events-none absolute left-[-1px] top-[-1px] h-[calc(100%-40px)] w-12 bg-gradient-to-r from-[#F2F8FF] from-20% to-transparent md:bottom-0 md:h-auto md:w-16" />
      )}

      {!isAtEnd && (
        <div className="pointer-events-none absolute right-[-1px] top-[-1px] h-[calc(100%-40px)] w-12 bg-gradient-to-l from-[#F2F8FF] from-20% to-transparent md:bottom-0 md:h-auto md:w-16" />
      )}

      <button
        aria-label="Scroll left"
        className={classNames(desktopButtonClass(isAtStart), 'absolute left-3 top-1/2 -translate-y-1/2')}
        onClick={scrollLeft}
        disabled={isAtStart}
        type="button"
      >
        <FaChevronLeft size={14} />
      </button>

      <button
        aria-label="Scroll right"
        className={classNames(desktopButtonClass(isAtEnd), 'absolute right-3 top-1/2 -translate-y-1/2')}
        onClick={scrollRight}
        disabled={isAtEnd}
        type="button"
      >
        <FaChevronRight size={14} />
      </button>

      {/* Mobile controls at bottom */}
      <div className="flex md:hidden gap-2 mt-3 px-2">
        <button
          aria-label="Scroll left"
          className={mobileButtonClass(isAtStart)}
          onClick={scrollLeft}
          disabled={isAtStart}
          type="button"
        >
          <FaChevronLeft size={14} />
        </button>
        <button
          aria-label="Scroll right"
          className={mobileButtonClass(isAtEnd)}
          onClick={scrollRight}
          disabled={isAtEnd}
          type="button"
        >
          <FaChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default NewsLayout;
